import { useState, useEffect, useRef } from "react";
import { isG80Car } from "../../../constants/vehicleConstants";
import { API_ENDPOINTS } from "../../../config/constants";

export const useVehicleImages = (vehicle) => {
  const [showImageForm, setShowImageForm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [approvedImages, setApprovedImages] = useState(
    vehicle.images?.filter((img) => img.approvedAt) || []
  );
  const [imageUrl, setImageUrl] = useState("");
  const [imageMessage, setImageMessage] = useState("");
  const [mediaErrors, setMediaErrors] = useState({});
  const loadingImages = useRef(new Set());

  useEffect(() => {
    setApprovedImages(vehicle.images?.filter((img) => img.approvedAt) || []);
    setCurrentImageIndex(0);
    // Clear error states when vehicle changes
    setMediaErrors({});
    loadingImages.current.clear();
  }, [vehicle]);

  const handleImageNav = (direction) => {
    if (direction === "next") {
      setCurrentImageIndex((prev) =>
        prev === approvedImages.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentImageIndex((prev) =>
        prev === 0 ? approvedImages.length - 1 : prev - 1
      );
    }
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
  };

  const handleMediaError = (imageId) => {
    // Only set error if the image was actually trying to load
    if (loadingImages.current.has(imageId)) {
      console.log(`Image load error for ${imageId}`);
      setMediaErrors((prev) => ({ ...prev, [imageId]: true }));
      loadingImages.current.delete(imageId);
    }
  };

  const handleMediaLoad = (imageId) => {
    setMediaErrors((prev) => ({ ...prev, [imageId]: false }));
    loadingImages.current.delete(imageId);
  };

  const handleMediaLoadStart = (imageId) => {
    console.log(`Image started loading: ${imageId}`);
    loadingImages.current.add(imageId);
    // Clear any existing error state when starting to load
    setMediaErrors((prev) => ({ ...prev, [imageId]: false }));
  };

  const isVideoUrl = (url) => {
    // Check for video file extensions
    const videoExtensions = [".mp4", ".webm", ".mov", ".avi"];
    const lowerUrl = url.toLowerCase();

    // Find the image object that matches this URL
    const imageObject = approvedImages.find((img) => img.url === url);

    return (
      videoExtensions.some((ext) => lowerUrl.includes(ext)) || // URL pattern
      lowerUrl.includes("video") || // URL contains "video"
      (imageObject && imageObject.isVideo) // Check isVideo flag for the specific image
    );
  };

  const hasG80Badge =
    vehicle.basicInfo &&
    isG80Car(
      vehicle.basicInfo.make,
      vehicle.basicInfo.model,
      vehicle.g80ConversionKit === "Yes"
    );
  const hasG80Kit = vehicle.g80ConversionKit === "Yes";

  const handleSetPrimary = async (imageId) => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.VEHICLES}/${vehicle._id}/images/${imageId}/set-primary`,
        {
          method: "PATCH",
          credentials: "include",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to set primary image");
      }

      const updatedVehicle = await response.json();
      setApprovedImages(updatedVehicle.images.filter((img) => img.approvedAt));
      setCurrentImageIndex(0); // Reset to first image since primary is now first
      setImageMessage("Primary image updated successfully");
    } catch (err) {
      console.error("Error setting primary image:", err);
      setImageMessage("Error setting primary image");
    }
  };

  return {
    showImageForm,
    setShowImageForm,
    currentImageIndex,
    setCurrentImageIndex,
    approvedImages,
    setApprovedImages,
    imageUrl,
    setImageUrl,
    imageMessage,
    setImageMessage,
    mediaErrors,
    handleImageNav,
    handleThumbnailClick,
    handleMediaError,
    handleMediaLoad,
    handleMediaLoadStart,
    isVideoUrl,
    hasG80Badge,
    hasG80Kit,
    handleSetPrimary,
  };
};
